import Cookies from 'js-cookie';
import { clearFulfilmentData } from '~/composables/clearFulfilmentData';
import { clearAllAuthCookies, setCookie } from '~/composables/useCookieManager';
import { useClientsStore } from '~/stores/clients';
import type {
  ApiResponse,
  ErrorResponse,
  LogoutResponse,
  NuxtAppWithClientAccount,
  UserStoreState,
} from '~/types/user';

/**
 * User store for managing authentication, user data, and company relationships
 */
export const useUserStore = defineStore('user', {
  state: (): UserStoreState => ({
    access_token: null, // Sanctum access token
    bearer_token: null, // Passport bearer token
  }),
  actions: {
    /**
     * Authenticate user with email and password
     */
    async login(
      email: string,
      password: string
    ): Promise<ErrorResponse | undefined> {
      const { $clientAccount } =
        useNuxtApp() as unknown as NuxtAppWithClientAccount;
      // make an API call to login the user with an email address and password
      try {
        const response = await $clientAccount.auth.login({
          email,
          password,
        });
        this.access_token = response.data.access_token;
        if (this.access_token) {
          // Security: Use 'session' type for authentication tokens (7-day expiry, Strict SameSite)
          setCookie('access_token', this.access_token, 'session');
        }
        return undefined;
      } catch {
        return { status: 'error', message: 'login failed' };
      }
    },
    async me(): Promise<void> {
      const auth = useAuth();

      if (!auth.loggedIn) return;

      // Ensure the user is loaded (e.g. on a fresh reload where only the token cookie exists).
      if (!auth.user) {
        try {
          await auth.fetchUser();
        } catch {
          // Token likely expired — try one refresh, then refetch.
          const { performTokenRefresh } =
            await import('~/composables/useTokenRefresh');
          if (await performTokenRefresh()) {
            await auth.fetchUser().catch(() => {});
          }
        }
      }

      if (auth.user) {
        await this.setUserLanguage(auth.user as Record<string, unknown>);
      }
    },
    /**
     * Refresh the access token using Laravel Passport OAuth2 refresh token flow
     * Uses the shared performTokenRefresh utility to prevent race conditions
     */
    async refresh(): Promise<ApiResponse<{ access_token: string }> | false> {
      const { performTokenRefresh } =
        await import('~/composables/useTokenRefresh');

      const success = await performTokenRefresh();

      if (success) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const auth = useAuth() as any;
        const token = auth.strategy.token.get();
        // Extract token without 'Bearer ' prefix
        const accessToken = token?.replace(/^Bearer\s+/i, '') || '';
        return {
          status: 200,
          data: { access_token: accessToken },
        };
      }

      clearAllAuthCookies();
      return false;
    },
    /**
     * Logout user and redirect to accounts logout
     * Ensures all authentication cookies are properly invalidated
     */
    async logout(): Promise<ErrorResponse | undefined> {
      const { $clientAccount } =
        useNuxtApp() as unknown as NuxtAppWithClientAccount;
      try {
        const response: ApiResponse<LogoutResponse> =
          await $clientAccount.auth.logout();
        const clientsStore = useClientsStore();
        clientsStore.resetStore();
        clearFulfilmentData();

        // Security: Clear all authentication cookies to prevent token reuse
        clearAllAuthCookies();

        if (response?.data?.redirect) {
          window.location.href = response.data.redirect;
        }
        return undefined;
      } catch {
        // Even if logout API fails, clear local cookies for security
        clearAllAuthCookies();
        return { status: 'error', message: 'logout failed' };
      }
    },
    /**
     * Set user language with i18n and cookie persistence
     */
    async setUserLanguage(authUser: Record<string, unknown>): Promise<void> {
      // Use the Nuxt i18n instance rather than useI18n(): this action also runs from a
      // global middleware (user-load.global), where useI18n() has no setup context.
      const { $i18n } = useNuxtApp();
      // eslint-disable-next-line no-unused-vars
      const i18n = $i18n as { setLocale: (locale: string) => Promise<void> };
      const runtimeConfig = useRuntimeConfig();

      // Extract and validate language
      const rawLanguage = authUser.language;
      if (typeof rawLanguage !== 'string') {
        return;
      }

      // Check if language is supported by i18n (currently only 'en' and 'nl')
      const supportedLanguages: ('en' | 'nl')[] = ['en', 'nl'];
      if (supportedLanguages.includes(rawLanguage as 'en' | 'nl')) {
        await i18n.setLocale(rawLanguage as 'en' | 'nl');

        // Save language cookie to custom domain if configured
        const cookieOptions: {
          expires: number;
          sameSite: 'Lax';
          path: string;
          secure: boolean;
          domain?: string;
        } = {
          expires: 365,
          sameSite: 'Lax',
          path: '/',
          secure:
            typeof window !== 'undefined'
              ? window.location.protocol === 'https:'
              : false,
        };

        // Use custom domain for language cookie if configured
        if (runtimeConfig.public.cookieDomain) {
          cookieOptions.domain = runtimeConfig.public.cookieDomain;
        }

        Cookies.set('language', rawLanguage, cookieOptions);
      }
    },
  },
});
